import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Product, CreateProductFormData, ProductType } from '../../types/product';
import { useProducts } from '../../hooks/useProducts';
import { useOrganizations } from '../../hooks/useOrganizations';
import { useInventory } from '../../hooks/useInventory';
import {
  CubeIcon,
  WrenchScrewdriverIcon,
  PlusIcon,
  FunnelIcon,
  TagIcon,
  PencilSquareIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  EyeIcon,
  PhotoIcon,
  BuildingStorefrontIcon,
  ArchiveBoxIcon
} from '@heroicons/react/24/outline';
import SearchBar from '../common/SearchBar';
import ProductForm from './ProductForm';
import ProductView from './ProductView';
import { Tooltip } from '../common/Tooltip';
import { useLanguage } from 'contexts/LanguageContext';
import Pagination from '../common/Pagination';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';
import Modal from '../common/Modal';
import { useCategories } from '../../hooks/useCategories';
import ImagePreview from '../common/ImagePreview';
import ImageModal from '../common/ImageModal';
import LoadingSpinner from '../common/LoadingSpinner';
import { toast } from 'react-hot-toast';

interface ProductListProps {
  selectedOrg: string;
}

// Fájl elején a kód vágólapra másolásához szükséges függvény
const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text)
    .then(() => {
      // Toast notifikáció használata alert helyett
      toast.success('Kód másolva a vágólapra!', {
        duration: 2000,
        position: 'bottom-center'
      });
    })
    .catch(err => {
      console.error('Nem sikerült másolni:', err);
      toast.error('Nem sikerült másolni a kódot', {
        duration: 2000,
        position: 'bottom-center'
      });
    });
};

const ProductList: React.FC<ProductListProps> = ({ selectedOrg }) => {
  const { products, loading: productsLoading, createProduct, updateProduct, deleteProduct } = useProducts();
  const { organizations } = useOrganizations();
  const { categories, mainCategories } = useCategories();
  const { inventory, loading: inventoryLoading, fetchInventory } = useInventory();
  const navigate = useNavigate();
  const [typeFilter, setTypeFilter] = useState<ProductType | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);
  const { t } = useLanguage();
  
  // Product view state
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [productToView, setProductToView] = useState<Product | null>(null);

  // Új filter: kép nélküli termékek
  const [noImageFilter, setNoImageFilter] = useState<boolean>(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Bulk selection state
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [isConfirmBulkDeleteOpen, setIsConfirmBulkDeleteOpen] = useState(false);
  const [isBulkOrgAssignOpen, setIsBulkOrgAssignOpen] = useState(false);
  const [bulkOrgId, setBulkOrgId] = useState<string>('');

  // Készlet adatok betöltése
  React.useEffect(() => {
    fetchInventory({});
  }, []);

  // Készlet összegzés termékenkét
  const productInventorySummary = useMemo(() => {
    const summary: Record<string, { total: number; available: number; warehouses: Array<{name: string, quantity: number, available: number}> }> = {};
    
    inventory.forEach(item => {
      if (!summary[item.productId]) {
        summary[item.productId] = { total: 0, available: 0, warehouses: [] };
      }
      const available = Math.max(0, (item.quantity || 0) - (item.reservedQuantity || 0));
      summary[item.productId].total += item.quantity || 0;
      summary[item.productId].available += available;
      summary[item.productId].warehouses.push({
        name: item.warehouseName || 'Ismeretlen raktár',
        quantity: item.quantity || 0,
        available: available
      });
    });
    
    return summary;
  }, [inventory]);

  // Készlet oldal megnyitása a termékhez
  const handleInventoryClick = (productId: string) => {
    navigate(`/inventory?productId=${productId}`);
  };

  // Get selected category name for display
  const selectedCategoryName = useMemo(() => {
    if (!categoryFilter) return null;
    
    // Try to find it as a main category
    const mainCategory = categories.find(c => c.id === categoryFilter);
    if (mainCategory) return mainCategory.name;
    
    // Try to find it as a subcategory
    const subcategory = categories.find(c => c.id === categoryFilter);
    if (subcategory) return subcategory.fullPath;
    
    return null;
  }, [categoryFilter, categories]);

  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      const matchesSearch =
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesType = typeFilter === 'all' || product.type === typeFilter;
      
      // Organization filter logic
      const matchesOrg = selectedOrg === 'all' || 
        product.organizationId === selectedOrg || 
        (!product.organizationId && selectedOrg === 'all');
      
      // Category filter logic
      let matchesCategory = !categoryFilter;
      
      if (categoryFilter) {
        const selectedCategory = categories.find(c => c.id === categoryFilter);
        
        if (selectedCategory?.parentId) {
          // Ha alkategória van kiválasztva, csak az alkategória termékeit mutassuk
          // Támogatjuk a régi formátumot is (categoryId + level2) és az új formátumot (subCategoryId)
          matchesCategory = 
            product.category?.subCategoryId === categoryFilter ||
            (product.category?.categoryId === selectedCategory.parentId && 
             product.category?.level2 === selectedCategory.name);
        } else {
          // Ha főkategória van kiválasztva, mutassuk a főkategória és az összes alkategória termékeit
          matchesCategory = 
            product.category?.categoryId === categoryFilter || 
            product.category?.subCategoryId === categoryFilter;
        }
      }
        
      // Kép nélküli termékek szűrése
      const matchesNoImage = !noImageFilter || 
        (!product.thumbnailUrl || product.thumbnailUrl === '');

      return matchesSearch && matchesType && matchesOrg && matchesCategory && matchesNoImage;
    });
  }, [products, searchTerm, typeFilter, selectedOrg, categoryFilter, noImageFilter, categories]); 

  // Kép nélküli termékek számolása
  const productsWithoutImage = useMemo(() => {
    return products.filter(product => !product.thumbnailUrl || product.thumbnailUrl === '').length;
  }, [products]);

  // Pagination logic
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleCreateProduct = async (data: CreateProductFormData) => {
    try {
      await createProduct(data);
      setIsModalOpen(false);
      toast.success('Termék sikeresen létrehozva!', {
        duration: 3000,
        position: 'top-right'
      });
    } catch (error) {
      console.error('Error creating product:', error);
      toast.error('Hiba történt a termék létrehozása során', {
        duration: 4000,
        position: 'top-right'
      });
    }
  };

  const handleUpdateProduct = async (data: CreateProductFormData) => {
    if (!selectedProduct) return;
    try {
      await updateProduct(selectedProduct.id, data);
      setIsModalOpen(false);
      setSelectedProduct(null);
      toast.success('Termék sikeresen frissítve!', {
        duration: 3000,
        position: 'top-right'
      });
    } catch (error) {
      console.error('Error updating product:', error);
      toast.error('Hiba történt a termék frissítése során', {
        duration: 4000,
        position: 'top-right'
      });
    }
  };

  const handleEditClick = (product: Product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const handleViewClick = (product: Product) => {
    setProductToView(product);
    setIsViewModalOpen(true);
  };

  // Bulk selection handlers
  const handleSelectProduct = (productId: string) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProducts(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedProducts.size === currentProducts.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(currentProducts.map(p => p.id)));
    }
  };

  const handleBulkDelete = async () => {
    try {
      const deletePromises = Array.from(selectedProducts).map(id => deleteProduct(id));
      await Promise.all(deletePromises);
      setSelectedProducts(new Set());
      setIsConfirmBulkDeleteOpen(false);
      toast.success(`${selectedProducts.size} termék törölve`);
    } catch (error) {
      console.error('Error deleting products:', error);
      toast.error('Hiba történt a törlés során');
    }
  };

  const handleBulkOrgAssign = async () => {
    try {
      const updatePromises = Array.from(selectedProducts).map(async (id) => {
        const product = products.find(p => p.id === id);
        if (product) {
          await updateProduct(id, { organizationId: bulkOrgId || undefined });
        }
      });
      await Promise.all(updatePromises);
      setSelectedProducts(new Set());
      setIsBulkOrgAssignOpen(false);
      setBulkOrgId('');
      toast.success(`${selectedProducts.size} termék szervezethez rendelve`);
    } catch (error) {
      console.error('Error assigning organization:', error);
      toast.error('Hiba történt a szervezet hozzárendelése során');
    }
  };

  const getInitialFormData = (product: Product): CreateProductFormData & { id: string } => ({
    id: product.id,
    type: product.type,
    code: product.code,
    name: product.name,
    description: product.description || '',
    unit: product.unit,
    isActive: product.isActive,
    ...(product.organizationId && { organizationId: product.organizationId }),
    ...(product.category && { 
      category: {
        ...product.category,
        // Biztosítsuk, hogy a subCategoryId is be legyen állítva, ha van level2
        ...(product.category.level2 && !product.category.subCategoryId && {
          subCategoryId: categories.find(c => 
            c.parentId === product.category?.categoryId && 
            c.name === product.category?.level2
          )?.id
        })
      }
    }),
    ...(product.netPrice !== undefined && { netPrice: product.netPrice }),
    ...(product.grossPrice !== undefined && { grossPrice: product.grossPrice }),
    ...(product.taxRate && { taxRate: product.taxRate }),
    ...(product.barcode && { barcode: product.barcode }),
    ...(product.webStore !== undefined && { webStore: product.webStore }),
    ...(product.reverseTaxation !== undefined && { reverseTaxation: product.reverseTaxation }),
    ...(product.isService !== undefined && { isService: product.isService }),
    ...(product.thumbnailUrl && { thumbnailUrl: product.thumbnailUrl }),
    ...(product.images && product.images.length > 0 && { images: product.images }),
    ...(product.hiddenImages && product.hiddenImages.length > 0 && { hiddenImages: product.hiddenImages }),
    ...(product.discontinued !== undefined && { discontinued: product.discontinued }),
    ...(product.notes && { notes: product.notes }),
    ...(product.vtsz && { vtsz: product.vtsz }),
    ...(product.knCode && { knCode: product.knCode }),
    ...(product.accountingPrice !== undefined && { accountingPrice: product.accountingPrice }),
    ...(product.purchasePrice !== undefined && { purchasePrice: product.purchasePrice }),
    ...(product.warrantyDays !== undefined && { warrantyDays: product.warrantyDays }),
    ...(product.manufacturer && { manufacturer: product.manufacturer }),
    ...(product.supplierCode && { supplierCode: product.supplierCode }),
    ...(product.articleNumber && { articleNumber: product.articleNumber }),
    ...(product.serialNumber !== undefined && { serialNumber: product.serialNumber }),
    ...(product.mandatoryLot !== undefined && { mandatoryLot: product.mandatoryLot }),
    ...(product.mandatoryExpiry !== undefined && { mandatoryExpiry: product.mandatoryExpiry }),
    ...(product.mandatorySize !== undefined && { mandatorySize: product.mandatorySize }),
    ...(product.mandatoryColor !== undefined && { mandatoryColor: product.mandatoryColor }),
    ...(product.secondaryUnit && { secondaryUnit: product.secondaryUnit }),
    ...(product.conversionRate !== undefined && { conversionRate: product.conversionRate }),
    ...(product.secondaryUnitRounding !== undefined && { secondaryUnitRounding: product.secondaryUnitRounding }),
    ...(product.dimensions && { dimensions: product.dimensions }),
    ...(product.translations && { translations: product.translations }),
    ...(product.packageDetails && { packageDetails: product.packageDetails }),
    organizations: product.organizationProducts?.map(op => ({
      organizationId: op.organizationId,
      price: op.price,
      currency: op.currency,
      taxRate: op.taxRate,
      ...(op.validFrom && { validFrom: op.validFrom }),
      ...(op.validUntil && { validUntil: op.validUntil }),
      ...(op.discounts && { discounts: op.discounts })
    })) || []
  });

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="p-3 sm:p-4 border-b border-gray-200 dark:border-gray-600">
          {/* Kereső és gombok */}
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <div className="w-full sm:flex-1 order-2 sm:order-1">
              <SearchBar
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder={t('common.placeholders.selectProduct')}
              />
            </div>
            <div className="flex items-center gap-2 order-1 sm:order-2">
              {selectedProducts.size > 0 && (
                <>
                  <button
                    onClick={() => setIsBulkOrgAssignOpen(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors text-sm flex items-center"
                  >
                    <svg className="w-5 h-5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    Szervezet ({selectedProducts.size})
                  </button>
                  <button
                    onClick={() => setIsConfirmBulkDeleteOpen(true)}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors text-sm flex items-center"
                  >
                    <TrashIcon className="w-5 h-5 mr-1.5" />
                    Törlés ({selectedProducts.size})
                  </button>
                </>
              )}
              <button
                onClick={() => {
                  setSelectedProduct(null);
                  setIsModalOpen(true);
                }}
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-3 sm:px-4 sm:py-2 rounded-lg transition-colors text-sm sm:text-base flex items-center w-full sm:w-auto justify-center"
              >
                <PlusIcon className="w-5 h-5 mr-1.5" />
                {t('products.new')}
              </button>
            </div>
          </div>
          
          {/* Típus szűrők - mobil optimalizált */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-1.5 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
            <button
              onClick={() => setTypeFilter('all')}
              className={`flex-none inline-flex items-center px-3 py-2 sm:px-2 sm:py-1 rounded-lg border text-sm sm:text-xs whitespace-nowrap
                ${typeFilter === 'all'
                  ? 'border-teal-500 dark:border-teal-400 bg-teal-500/20 dark:bg-teal-500/30 text-teal-600 dark:text-teal-400'
                  : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
            >
              <FunnelIcon className="w-4 h-4 sm:w-3.5 sm:h-3.5 mr-1.5 sm:mr-1" />
              {t('products.filter.allTypes')}
            </button>
            <button
              onClick={() => setTypeFilter('product')}
              className={`flex-none inline-flex items-center px-3 py-2 sm:px-2 sm:py-1 rounded-lg border text-sm sm:text-xs whitespace-nowrap
                ${typeFilter === 'product'
                  ? 'border-blue-500 dark:border-blue-400 bg-blue-500/20 dark:bg-blue-500/30 text-blue-600 dark:text-blue-400'
                  : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
            >
              <CubeIcon className="w-4 h-4 sm:w-3.5 sm:h-3.5 mr-1.5 sm:mr-1" />
              {t('products.filter.products')}
            </button>
            <button
              onClick={() => setTypeFilter('service')}
              className={`flex-none inline-flex items-center px-3 py-2 sm:px-2 sm:py-1 rounded-lg border text-sm sm:text-xs whitespace-nowrap
                ${typeFilter === 'service'
                  ? 'border-purple-500 dark:border-purple-400 bg-purple-500/20 dark:bg-purple-500/30 text-purple-600 dark:text-purple-400'
                  : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
            >
              <WrenchScrewdriverIcon className="w-4 h-4 sm:w-3.5 sm:h-3.5 mr-1.5 sm:mr-1" />
              {t('products.filter.services')}
            </button>
            <button
              onClick={() => setTypeFilter('package')}
              className={`flex-none inline-flex items-center px-3 py-2 sm:px-2 sm:py-1 rounded-lg border text-sm sm:text-xs whitespace-nowrap
                ${typeFilter === 'package'
                  ? 'border-green-500 dark:border-green-400 bg-green-500/20 dark:bg-green-500/30 text-green-600 dark:text-green-400'
                  : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
            >
              <TagIcon className="w-4 h-4 sm:w-3.5 sm:h-3.5 mr-1.5 sm:mr-1" />
              {t('products.filter.packages')}
            </button>
            
            {/* Kép nélküli termékek szűrője */}
            <button
              onClick={() => setNoImageFilter(!noImageFilter)}
              className={`flex-none inline-flex items-center px-3 py-2 sm:px-2 sm:py-1 rounded-lg border text-sm sm:text-xs whitespace-nowrap
                ${noImageFilter
                  ? 'border-red-500 dark:border-red-400 bg-red-500/20 dark:bg-red-500/30 text-red-600 dark:text-red-400'
                  : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
            >
              <PhotoIcon className="w-4 h-4 sm:w-3.5 sm:h-3.5 mr-1.5 sm:mr-1" />
              Kép nélküliek 
              <span className="ml-1.5 sm:ml-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-full px-2 py-0.5 sm:px-1.5 sm:py-0.5 text-xs sm:text-[10px]">
                {productsWithoutImage}
              </span>
            </button>
          </div>
          
          {/* Kép nélküli termékek információ sáv */}
          {noImageFilter && (
            <div className="mt-3 sm:mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 bg-red-50 dark:bg-red-900/20 rounded-md p-3 sm:p-2 text-sm sm:text-xs text-red-600 dark:text-red-400">
              <div className="flex items-center">
                <PhotoIcon className="w-5 h-5 sm:w-4 sm:h-4 mr-2 sm:mr-1.5" />
                <span>
                  Összesen <strong>{productsWithoutImage}</strong> termék van kép nélkül. 
                  {filteredProducts.length !== productsWithoutImage && (
                    <span> A további szűrésekkel <strong>{filteredProducts.length}</strong> termék látható.</span>
                  )}
                </span>
              </div>
            </div>
          )}
          
          {/* Kategória szűrők - mobil optimalizált */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-1.5 mt-3 sm:mt-2 pt-3 sm:pt-2 border-t border-gray-100 dark:border-gray-700">
            <div className="flex items-center mr-2">
              <TagIcon className="w-4 h-4 sm:w-3.5 sm:h-3.5 text-gray-500 dark:text-gray-400 mr-1.5 sm:mr-1" />
              <span className="text-sm sm:text-xs text-gray-500 dark:text-gray-400">{t('products.fields.category')}:</span>
            </div>
            
            <button
              onClick={() => setCategoryFilter(null)}
              className={`flex-none inline-flex items-center px-3 py-2 sm:px-2 sm:py-1 rounded-lg border text-sm sm:text-xs whitespace-nowrap
                ${categoryFilter === null
                  ? 'border-orange-500 dark:border-orange-400 bg-orange-500/20 dark:bg-orange-500/30 text-orange-600 dark:text-orange-400 font-medium'
                  : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
            >
              {t('products.filter.allTypes')}
            </button>
            
            {mainCategories.map(mainCat => (
              <button
                key={mainCat.id}
                onClick={() => setCategoryFilter(mainCat.id)}
                className={`flex-none inline-flex items-center px-3 py-2 sm:px-2 sm:py-1 rounded-lg border text-sm sm:text-xs whitespace-nowrap
                  ${categoryFilter === mainCat.id || 
                    (categoryFilter && categories.find(c => c.id === categoryFilter)?.parentId === mainCat.id)
                    ? 'border-orange-500 dark:border-orange-400 bg-orange-500/20 dark:bg-orange-500/30 text-orange-600 dark:text-orange-400 font-medium'
                    : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500'
                  }`}
              >
                {mainCat.name}
              </button>
            ))}
          </div>
          
          {/* Alkategória szűrők - csak ha van kiválasztott főkategória */}
          {categoryFilter && !categories.find(c => c.id === categoryFilter)?.parentId && (
            <div className="flex flex-wrap items-center gap-2 sm:gap-1.5 mt-3 sm:mt-2 pt-3 sm:pt-2 border-t border-gray-100">
              <span className="text-sm sm:text-xs text-gray-500 mr-2">{t('products.fields.category2')}:</span>
              
              {mainCategories
                .find(cat => cat.id === categoryFilter)
                ?.subcategories
                ?.map(subCat => (
                  <button
                    key={subCat.id}
                    onClick={() => setCategoryFilter(subCat.id)}
                    className={`flex-none inline-flex items-center px-3 py-2 sm:px-2 sm:py-1 rounded-lg border text-sm sm:text-xs whitespace-nowrap
                      ${categoryFilter === subCat.id
                        ? 'border-blue-500 bg-blue-500/20 text-blue-600 font-medium'
                        : 'border-gray-300 text-gray-600 hover:border-gray-400'
                      }`}
                  >
                    {subCat.name}
                  </button>
                ))}
            </div>
          )}
          
          {/* Ha egy alkategória van kiválasztva, akkor is jelenítsük meg az alkategóriákat */}
          {categoryFilter && categories.find(c => c.id === categoryFilter)?.parentId && (
            <div className="flex flex-wrap items-center gap-2 sm:gap-1.5 mt-3 sm:mt-2 pt-3 sm:pt-2 border-t border-gray-100">
              <span className="text-sm sm:text-xs text-gray-500 mr-2">{t('products.fields.category2')}:</span>
              
              {mainCategories
                .find(cat => cat.id === categories.find(c => c.id === categoryFilter)?.parentId)
                ?.subcategories
                ?.map(subCat => (
                  <button
                    key={subCat.id}
                    onClick={() => setCategoryFilter(subCat.id)}
                    className={`flex-none inline-flex items-center px-3 py-2 sm:px-2 sm:py-1 rounded-lg border text-sm sm:text-xs whitespace-nowrap
                      ${categoryFilter === subCat.id
                        ? 'border-blue-500 bg-blue-500/20 text-blue-600 font-medium'
                        : 'border-gray-300 text-gray-600 hover:border-gray-400'
                      }`}
                  >
                    {subCat.name}
                  </button>
                ))}
            </div>
          )}
        </div>
        
        {/* Loading indikátor - az egész tartalmat helyettesíti, amikor tölt */}
        {productsLoading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <LoadingSpinner size="lg" className="text-primary-500 mb-4" />
            <p className="text-gray-600 dark:text-gray-400 font-medium">{t('products.loading') || 'Termékek betöltése...'}</p>
          </div>
        ) : (
          <>
            {/* Ha nincs találat */}
            {filteredProducts.length === 0 ? (
              <div className="text-center py-12">
                <div className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-3">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                </div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('products.noResults')}</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('products.tryDifferentSearch')}</p>
              </div>
            ) : (
              // Desktop táblázat - egyszerűsített oszlopokkal
              <div className="hidden lg:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-3 py-2 text-center w-12">
                        <input
                          type="checkbox"
                          checked={selectedProducts.size === currentProducts.length && currentProducts.length > 0}
                          onChange={handleSelectAll}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('products.fields.product')}</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('products.fields.category')}</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('products.fields.stock')}</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('products.fields.price')}</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-20">{t('products.fields.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {currentProducts.map((product, index) => (
                      <tr key={product.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer" 
                        onClick={(e) => {
                          const clickedElement = e.target as HTMLElement;
                          const isImageClick = clickedElement.closest('.image-modal-container');
                          const isActionButton = clickedElement.closest('button');
                          
                          if (!isImageClick && !isActionButton) {
                            handleEditClick(product);
                          }
                        }}
                      >
                        <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedProducts.has(product.id)}
                            onChange={() => handleSelectProduct(product.id)}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center space-x-3">
                            {product.thumbnailUrl ? (
                              <div className="relative flex-shrink-0 image-modal-container">
                                <ImageModal 
                                  src={product.thumbnailUrl} 
                                  alt={product.name}
                                  thumbnailClassName="w-16 h-16 rounded-md border border-gray-200 overflow-hidden object-cover"
                                  highQualitySrc={product.images?.find(img => img.isMain)?.url || product.thumbnailUrl}
                                />
                              </div>
                            ) : (
                              <div className={`w-16 h-16 flex items-center justify-center rounded-md bg-gradient-to-br ${product.type === 'product'
                                ? 'from-blue-500/20 to-blue-600/20 border-blue-500/20'
                                : 'from-purple-500/20 to-purple-600/20 border-purple-500/20'
                                } border flex-shrink-0 relative`}>
                                {product.type === 'product' ? (
                                  <CubeIcon className="h-6 w-6 text-blue-600" />
                                ) : (
                                  <WrenchScrewdriverIcon className="h-6 w-6 text-purple-600" />
                                )}
                                
                                {/* Kép nélküli termékek sorszámozása */}
                                {noImageFilter && (
                                  <div className="absolute top-1 right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                    {indexOfFirstItem + index}
                                  </div>
                                )}
                              </div>
                            )}
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{product.name}</div>
                              <div className="flex items-center gap-2">
                                <div 
                                  className="text-xs text-gray-600 dark:text-gray-400 flex items-center overflow-hidden cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-primary-600 px-1 py-0.5 rounded transition-colors w-fit group"
                                  onClick={(e) => {
                                    e.stopPropagation(); 
                                    copyToClipboard(product.code);
                                  }}
                                  title="Kattints a kód másolásához"
                                >
                                  <TagIcon className="h-3 w-3 mr-1 flex-shrink-0 group-hover:text-primary-600" />
                                  <span className="truncate">{product.code}</span>
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                                    <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                                  </svg>
                                </div>
                                {product.organizationId && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-400">
                                    <BuildingStorefrontIcon className="h-3 w-3 mr-1" />
                                    {organizations.find(o => o.id === product.organizationId)?.name || 'Szervezet'}
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {!product.isActive && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-400">
                                    Inaktív
                                  </span>
                                )}
                                {product.webStore && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400">
                                    {t('products.fields.webStore')}
                                  </span>
                                )}
                                {product.discontinued && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400">
                                    {t('products.fields.discontinued')}
                                  </span>
                                )}
                                {product.taxRate && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400">
                                    {product.taxRate}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-xs text-gray-600 dark:text-gray-400 max-w-xs overflow-hidden">
                            {product.category?.fullPath || <span className="text-gray-400 dark:text-gray-500 italic">-</span>}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="space-y-1">
                            {productInventorySummary[product.id] ? (
                              <div>
                                <div className="flex items-center justify-end space-x-1">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleInventoryClick(product.id);
                                    }}
                                    className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-700"
                                    title="Készlet megtekintése"
                                  >
                                    <span className={productInventorySummary[product.id].available > 0 ? 'text-green-600' : 'text-red-600'}>
                                      {productInventorySummary[product.id].available}
                                    </span>
                                    {productInventorySummary[product.id].total !== productInventorySummary[product.id].available && (
                                      <span className="text-gray-500 ml-1">/ {productInventorySummary[product.id].total}</span>
                                    )}
                                    <BuildingStorefrontIcon className="w-3 h-3 ml-1" />
                                  </button>
                                </div>
                                <div className="flex flex-wrap gap-1 justify-end mt-1">
                                  {productInventorySummary[product.id].warehouses
                                    .filter(wh => wh.quantity > 0)
                                    .map((wh, idx) => (
                                    <div 
                                      key={idx} 
                                      className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${
                                        wh.available > 0 
                                          ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-700' 
                                          : 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-600'
                                      }`}
                                    >
                                      <span className="truncate max-w-[30px]">{wh.name}:</span>
                                      <span className="ml-0.5 font-bold">{wh.available}</span>
                                      {wh.quantity !== wh.available && (
                                        <span className="text-gray-400 dark:text-gray-500">/{wh.quantity}</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <div className="text-sm text-gray-400 dark:text-gray-500">
                                <span>-</span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="space-y-1">
                            {product.netPrice !== undefined && (
                              <div className="flex items-center justify-end space-x-1 text-xs">
                                <span className="text-gray-600 dark:text-gray-400">{t('products.fields.netPrice')}:</span>
                                <span className="text-gray-800 dark:text-gray-200 font-medium">
                                  {Number(product.netPrice).toLocaleString('hu-HU')} {product.currency || 'HUF'}
                                </span>
                              </div>
                            )}
                            {product.netPrice !== undefined && product.taxRate && (
                              <div className="flex items-center justify-end space-x-1 text-xs">
                                <span className="text-gray-600 dark:text-gray-400">{t('products.fields.grossPrice')}:</span>
                                <span className="text-gray-800 dark:text-gray-200 font-medium">
                                  {(product.taxRate === '27%' 
                                    ? Math.round(Number(product.netPrice) * 1.27) 
                                    : product.taxRate === '18%' 
                                      ? Math.round(Number(product.netPrice) * 1.18)
                                      : product.taxRate === '5%' 
                                        ? Math.round(Number(product.netPrice) * 1.05)
                                        : Number(product.netPrice)
                                  ).toLocaleString('hu-HU')} {product.currency || 'HUF'}
                                </span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-center space-x-2">
                            <Tooltip content={t('products.tooltips.view')}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewClick(product);
                                }}
                                className="text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>
                            </Tooltip>
                            <Tooltip content={t('products.tooltips.edit')}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditClick(product);
                                }}
                                className="text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                              >
                                <PencilSquareIcon className="h-4 w-4" />
                              </button>
                            </Tooltip>
                            <Tooltip content={t('products.tooltips.delete')}>
                              <button
                                onClick={() => {
                                  setProductToDelete(product);
                                  setIsConfirmDeleteOpen(true);
                                }}
                                className="text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </Tooltip>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Mobile cards - teljesen újraoptimalizált */}
      {!productsLoading && filteredProducts.length > 0 && (
        <div className="lg:hidden space-y-4 px-1">
          {currentProducts.map((product, index) => (
            <div
              key={product.id}
              className={`bg-white dark:bg-gray-800 rounded-xl border-l-4 ${product.type === 'product' ? 'border-blue-400' : 'border-purple-400'
                } shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden`}
            >
              {/* Fő tartalom terület */}
              <div className="p-4">
                <div className="flex items-start gap-3">
                  {/* Termék kép vagy ikon */}
                  {product.thumbnailUrl ? (
                    <div className="relative flex-shrink-0 image-modal-container">
                      <ImageModal 
                        src={product.thumbnailUrl} 
                        alt={product.name}
                        thumbnailClassName="w-16 h-16 rounded-lg border border-gray-200 overflow-hidden object-cover"
                        highQualitySrc={product.images?.find(img => img.isMain)?.url || product.thumbnailUrl}
                      />
                    </div>
                  ) : (
                    <div className={`w-16 h-16 flex items-center justify-center rounded-lg bg-gradient-to-br ${product.type === 'product'
                      ? 'from-blue-500/20 to-blue-600/20 border-blue-500/20'
                      : 'from-purple-500/20 to-purple-600/20 border-purple-500/20'
                      } border flex-shrink-0 relative`}>
                      {product.type === 'product' ? (
                        <CubeIcon className="h-8 w-8 text-blue-600" />
                      ) : (
                        <WrenchScrewdriverIcon className="h-8 w-8 text-purple-600" />
                      )}
                      
                      {/* Kép nélküli termékek sorszámozása */}
                      {noImageFilter && (
                        <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                          {indexOfFirstItem + index + 1}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Termék információk */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 leading-5 mb-1">
                      {product.name}
                    </h3>
                    
                    {/* Termékkód és szervezet - másolható */}
                    <div className="flex items-center gap-2 mb-2">
                      <div 
                        className="inline-flex items-center bg-gray-100 dark:bg-gray-700 hover:bg-primary-100 dark:hover:bg-primary-900/30 px-2 py-1 rounded-md cursor-pointer transition-colors group"
                        onClick={(e) => {
                          e.stopPropagation(); 
                          copyToClipboard(product.code);
                        }}
                        title="Kattints a kód másolásához"
                      >
                        <TagIcon className="h-3 w-3 mr-1 text-gray-500 dark:text-gray-400 group-hover:text-primary-600 dark:group-hover:text-primary-400" />
                        <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-primary-700 dark:group-hover:text-primary-400 font-medium">{product.code}</span>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-1 opacity-0 group-hover:opacity-100 text-primary-600 transition-opacity" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                          <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                        </svg>
                      </div>
                      {product.organizationId && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-400">
                          <BuildingStorefrontIcon className="h-3 w-3 mr-1" />
                          {organizations.find(o => o.id === product.organizationId)?.name || 'Szervezet'}
                        </span>
                      )}
                    </div>

                    {/* Kategória */}
                    {product.category?.fullPath && (
                      <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {product.category.fullPath}
                      </div>
                    )}

                    {/* Állapot címkék */}
                    <div className="flex flex-wrap gap-1 mb-3">
                      {!product.isActive && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-400">
                          Inaktív
                        </span>
                      )}
                      {product.webStore && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400">
                          {t('products.fields.webStore')}
                        </span>
                      )}
                      {product.discontinued && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400">
                          {t('products.fields.discontinued')}
                        </span>
                      )}
                      {product.taxRate && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400">
                          {product.taxRate}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Készlet és árak rész */}
                <div className="mt-4 space-y-3">
                  {/* Készlet információk */}
                  {productInventorySummary[product.id] ? (
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Készlet:</span>
                        <button
                          onClick={() => handleInventoryClick(product.id)}
                          className="inline-flex items-center text-sm font-semibold text-blue-600 hover:text-blue-700"
                        >
                          <span className={productInventorySummary[product.id].available > 0 ? 'text-green-600' : 'text-red-600'}>
                            {productInventorySummary[product.id].available}
                          </span>
                          {productInventorySummary[product.id].total !== productInventorySummary[product.id].available && (
                            <span className="text-gray-500 dark:text-gray-400 ml-1">/ {productInventorySummary[product.id].total}</span>
                          )}
                          <BuildingStorefrontIcon className="w-4 h-4 ml-1" />
                        </button>
                      </div>
                      
                      {/* Raktárak részletesen */}
                      <div className="flex flex-wrap gap-1">
                        {productInventorySummary[product.id].warehouses
                          .filter(wh => wh.quantity > 0)
                          .map((wh, idx) => (
                          <div 
                            key={idx} 
                            className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
                              wh.available > 0 
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' 
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                            }`}
                          >
                            <span className="font-semibold mr-1">{wh.name}:</span>
                            <span>{wh.available}</span>
                            {wh.quantity !== wh.available && (
                              <span className="text-gray-500">/{wh.quantity}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <span className="text-sm text-gray-500">Nincs készletadat</span>
                    </div>
                  )}

                  {/* Árak */}
                  {product.netPrice !== undefined && (
                    <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">{t('products.fields.netPrice')}:</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {Number(product.netPrice).toLocaleString('hu-HU')} {product.currency || 'HUF'}
                        </span>
                      </div>
                      
                      {product.taxRate && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">{t('products.fields.grossPrice')}:</span>
                          <span className="text-sm font-semibold text-gray-900">
                            {(product.taxRate === '27%' 
                              ? Math.round(Number(product.netPrice) * 1.27) 
                              : product.taxRate === '18%' 
                                ? Math.round(Number(product.netPrice) * 1.18)
                                : product.taxRate === '5%' 
                                  ? Math.round(Number(product.netPrice) * 1.05)
                                  : Number(product.netPrice)
                            ).toLocaleString('hu-HU')} {product.currency || 'HUF'}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Action buttons - nagyobb gombok mobilra */}
              <div className="grid grid-cols-4 border-t border-gray-200">
                <button
                  onClick={() => handleInventoryClick(product.id)}
                  className="flex flex-col items-center justify-center py-4 px-2 text-gray-600 hover:text-blue-600 active:bg-gray-100 border-r border-gray-200 transition-colors"
                >
                  <ArchiveBoxIcon className="h-5 w-5 mb-1" />
                  <span className="text-xs font-medium">Készlet</span>
                </button>
                <button
                  onClick={() => handleViewClick(product)}
                  className="flex flex-col items-center justify-center py-4 px-2 text-gray-600 hover:text-blue-600 active:bg-blue-50 border-r border-gray-200 transition-colors"
                >
                  <EyeIcon className="h-5 w-5 mb-1" />
                  <span className="text-xs font-medium">Megtekint</span>
                </button>
                <button
                  onClick={() => handleEditClick(product)}
                  className="flex flex-col items-center justify-center py-4 px-2 text-gray-600 hover:text-primary-600 active:bg-primary-50 border-r border-gray-200 transition-colors"
                >
                  <PencilSquareIcon className="h-5 w-5 mb-1" />
                  <span className="text-xs font-medium">Szerkeszt</span>
                </button>
                <button
                  onClick={() => {
                    setProductToDelete(product);
                    setIsConfirmDeleteOpen(true);
                  }}
                  className="flex flex-col items-center justify-center py-4 px-2 text-gray-600 hover:text-red-600 active:bg-red-50 transition-colors"
                >
                  <TrashIcon className="h-5 w-5 mb-1" />
                  <span className="text-xs font-medium">Törlés</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {filteredProducts.length > 0 && (
        <div className="relative z-10">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
            totalItems={filteredProducts.length}
            indexOfFirstItem={indexOfFirstItem}
            indexOfLastItem={indexOfLastItem}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </div>
      )}

      {/* Modals */}
      {isModalOpen && (
        <ProductForm
          isOpen={isModalOpen}
          onCancel={() => {
            setIsModalOpen(false);
            setSelectedProduct(null);
          }}
          onSubmit={selectedProduct ? handleUpdateProduct : handleCreateProduct}
          initialData={selectedProduct ? getInitialFormData(selectedProduct) : undefined}
        />
      )}

      {isViewModalOpen && productToView && (
        <ProductView
          product={productToView}
          isOpen={isViewModalOpen}
          onClose={() => {
            setIsViewModalOpen(false);
            setProductToView(null);
          }}
        />
      )}

      {isConfirmDeleteOpen && productToDelete && (
        <ConfirmDeleteModal
          isOpen={isConfirmDeleteOpen}
          onClose={() => {
            setIsConfirmDeleteOpen(false);
            setProductToDelete(null);
          }}
          onConfirm={async () => {
            if (productToDelete) {
              await deleteProduct(productToDelete.id);
              setIsConfirmDeleteOpen(false);
              setProductToDelete(null);
            }
          }}
          title={t('products.deleteConfirmTitle')}
          deleteMessage={productToDelete ? 
            `${t('products.deleteConfirmMessage')}: ${productToDelete.name}` : 
            t('products.deleteConfirmMessage')}
          deleteWarning={t('products.deleteWarning')}
        />
      )}

      {/* Bulk delete confirmation */}
      {isConfirmBulkDeleteOpen && (
        <ConfirmDeleteModal
          isOpen={isConfirmBulkDeleteOpen}
          onClose={() => {
            setIsConfirmBulkDeleteOpen(false);
          }}
          onConfirm={handleBulkDelete}
          title="Tömeges törlés megerősítése"
          deleteMessage={`Biztosan törölni szeretnéd a kiválasztott ${selectedProducts.size} terméket?`}
          deleteWarning="Ez a művelet nem vonható vissza!"
        />
      )}

      {/* Bulk organization assignment modal */}
      {isBulkOrgAssignOpen && (
        <Modal
          isOpen={isBulkOrgAssignOpen}
          onClose={() => {
            setIsBulkOrgAssignOpen(false);
            setBulkOrgId('');
          }}
          title="Szervezet hozzárendelése"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {selectedProducts.size} termék szervezethez rendelése
            </p>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Válassz szervezetet
              </label>
              <select
                value={bulkOrgId}
                onChange={(e) => setBulkOrgId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="">Nincs szervezet (Globális)</option>
                {organizations.map(org => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <button
                onClick={() => {
                  setIsBulkOrgAssignOpen(false);
                  setBulkOrgId('');
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Mégse
              </button>
              <button
                onClick={handleBulkOrgAssign}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
              >
                Hozzárendelés
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ProductList;